import { useEffect, useState } from 'react';
import { reaction, toJS, IReactionOptions } from 'mobx';

import { isEqual } from 'lodash';

/**
 * Делает поверхностное (shallow) слияние нового объекта со старым,
 * стараясь сохранить ссылки на неизменённые поля (включая те,
 * что "одинаковы" по содержимому).
 *
 * Если при сравнении поля выясняется, что:
 *  - у них та же самая ссылка (===), оставляем старую.
 *  - у них разные ссылки, но по содержимому (isEqual) совпадают, тоже оставляем старую.
 *  - иначе, поле меняем на новое.
 *
 * Если в сумме ни одно поле не изменилось (или кол-во ключей не совпало),
 * то возвращаем старый объект (не создаём новый).
 */
export function shallowMerge<T extends object>(oldObj: T, newObj: T): T {
  // Быстрый случай: объект целиком совпадает (глубоко).
  if (isEqual(oldObj, newObj)) {
    return oldObj; // возвращаем старую ссылку
  }

  const merged: any = Array.isArray(newObj) ? [] : {};
  let changed = false;

  // Проходимся по ключам нового объекта
  const newKeys = Object.keys(newObj);
  for (const key of newKeys) {
    const oldVal = (oldObj as any)[key];
    const newVal = (newObj as any)[key];

    // Если те же ссылки, оставляем старую
    if (Object.is(oldVal, newVal)) {
      merged[key] = oldVal;
    }
    // Если по содержимому равны, тоже берём старую ссылку
    else if (isEqual(oldVal, newVal)) {
      merged[key] = oldVal;
    }
    // Иначе меняем
    else {
      merged[key] = newVal;
      changed = true;
    }
  }

  // Если в старом объекте есть ключи, которых в новом нет – тоже считаем изменением
  const oldKeys = Object.keys(oldObj);
  if (oldKeys.length !== newKeys.length) {
    changed = true;
  }

  // Если оказалось, что ни одно поле не изменилось, вернём старый объект
  if (!changed) {
    return oldObj;
  }

  return merged as T;
}


/**
 * Хук для синхронизации mobx‑наблюдаемого значения с React‑стейтом,
 * при этом «сохраняет» ссылки на неизменённые поля (шаллоу‑подход).
 *
 * @param observable - mobx‑наблюдаемое значение (обычно объект).
 * @param options - опции для reaction (delay и пр.).
 *
 * @returns Состояние, которое будет иметь ту же структуру,
 * что вернул toJS(observable), но с сохранёнными ссылками на неизменённые поля.
 */
export function useMobxStoreToState<T extends object>(
  observable: T,
  options: IReactionOptions<T, boolean> = {}
): T {
  // Инициализируем состояние текущим значением observable (просто делаем toJS).
  const [value, setValue] = useState<T>(() => toJS(observable));

  useEffect(() => {
    // Создаём MobX-реакцию на все изменения внутри observable.
    const disposer = reaction(
      () => toJS(observable),
      (newObj: T) => {
        // При каждом изменении – мерджим shallow'ом с предыдущим значением:
        setValue((prev) => shallowMerge(prev, newObj));
      },
      options
    );

    return () => disposer();
  }, [observable, options]);

  return value;
}
